<template>
  <v-card class="widget-card" elevation="2">
    <v-card-title class="d-flex align-center justify-space-between pb-2">
      <div class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-message-text-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold"
          >Статистика диалогов</span
        >
      </div>
      <v-btn icon small @click="loadData" :loading="loading">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-0">
      <!-- Date filters -->
      <v-row dense class="mb-4">
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="dateFrom"
            label="С даты"
            type="date"
            dense
            outlined
            hide-details
            @change="loadData"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="dateTo"
            label="По дату"
            type="date"
            dense
            outlined
            hide-details
            @change="loadData"
          />
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-3"
      />
      <div v-if="error" class="red--text text-caption mb-3">{{ error }}</div>

      <template v-if="stats && !loading">
        <!-- KPI: ключевые метрики -->
        <v-row dense class="mb-4">
          <v-col cols="6" sm="3">
            <v-card outlined class="stat-chip text-center pa-2">
              <div class="text-h5 font-weight-bold primary--text">
                {{ stats.total_open_sessions }}
              </div>
              <div class="text-caption grey--text">Открытых диалогов</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="stat-chip text-center pa-2">
              <div class="text-h5 font-weight-bold warning--text">
                {{ stats.total_inactive_dialogs }}
              </div>
              <div class="text-caption grey--text">
                Неактивных (>{{ stats.inactive_threshold_days }}д)
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="stat-chip text-center pa-2">
              <div
                class="text-h5 font-weight-bold"
                :class="waitDaysColor(stats.overall_avg_wait_days)"
              >
                {{ stats.overall_avg_wait_days }}д
              </div>
              <div class="text-caption grey--text">Среднее ожидание ответа</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="stat-chip text-center pa-2">
              <div class="text-h5 font-weight-bold info--text">
                {{ stats.overall_avg_dialog_span_days }}д
              </div>
              <div class="text-caption grey--text">Сред. длит. диалога</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabs: статус/источник | операторы | каналы -->
        <v-tabs v-model="activeTab" dense height="32" class="mb-3">
          <v-tab class="text-caption">По статусу / источнику</v-tab>
          <v-tab class="text-caption">Операторы</v-tab>
          <v-tab class="text-caption">Каналы (линии)</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Tab 0: статус + источник -->
          <v-tab-item>
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="section-label">По статусу</div>
                <div
                  v-for="(count, status) in stats.by_status"
                  :key="status"
                  class="mb-2"
                >
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-caption">{{ status }}</span>
                    <span class="text-caption font-weight-bold">{{
                      count
                    }}</span>
                  </div>
                  <v-progress-linear
                    :value="pct(count, stats.total_open_sessions)"
                    :color="statusColor(status)"
                    height="5"
                    rounded
                  />
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="section-label">По источнику</div>
                <div
                  v-for="(count, source) in stats.by_source_type"
                  :key="source"
                  class="mb-2"
                >
                  <div class="d-flex justify-space-between align-center">
                    <span
                      class="text-caption text-truncate"
                      style="max-width: 150px"
                      >{{ source }}</span
                    >
                    <span class="text-caption font-weight-bold">{{
                      count
                    }}</span>
                  </div>
                  <v-progress-linear
                    :value="pct(count, stats.total_open_sessions)"
                    color="primary"
                    height="5"
                    rounded
                  />
                </div>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Tab 1: операторы -->
          <v-tab-item>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-caption">Оператор (ID)</th>
                    <th class="text-caption text-center">Диалогов</th>
                    <th class="text-caption text-center">Ср. ожидание</th>
                    <th class="text-caption text-center">Ср. длит. диалога</th>
                    <th class="text-caption text-center">Нагрузка</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="op in stats.by_operator_detailed"
                    :key="op.operator_id"
                  >
                    <td class="text-caption font-weight-medium">
                      <v-chip x-small label color="grey lighten-3">{{
                        op.operator_id || "Без оператора"
                      }}</v-chip>
                    </td>
                    <td class="text-caption text-center font-weight-bold">
                      {{ op.total_dialogs }}
                    </td>
                    <td class="text-caption text-center">
                      <v-chip
                        x-small
                        :color="waitDaysChipColor(op.avg_wait_days)"
                        dark
                        >{{ op.avg_wait_days }}д</v-chip
                      >
                    </td>
                    <td class="text-caption text-center">
                      {{ op.avg_dialog_span_days }}д
                    </td>
                    <td style="min-width: 80px">
                      <v-progress-linear
                        :value="
                          pct(op.total_dialogs, stats.total_open_sessions)
                        "
                        color="primary"
                        height="6"
                        rounded
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="text-caption grey--text mt-2">
              * Ожидание = среднее кол-во дней без активности в диалоге
            </div>
          </v-tab-item>

          <!-- Tab 2: каналы (линии) -->
          <v-tab-item>
            <div
              v-for="(count, line) in stats.by_line"
              :key="line"
              class="mb-2"
            >
              <div class="d-flex justify-space-between align-center">
                <span
                  class="text-caption text-truncate"
                  style="max-width: 200px"
                  >{{ line }}</span
                >
                <span class="text-caption font-weight-bold">{{ count }}</span>
              </div>
              <v-progress-linear
                :value="pct(count, stats.total_open_sessions)"
                color="teal"
                height="5"
                rounded
              />
            </div>
          </v-tab-item>
        </v-tabs-items>

        <div class="text-caption grey--text mt-3">
          Отчёт сформирован: {{ formatDate(stats.report_generated) }}
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import NSApi from "@/api/NSApi";

export default {
  name: "CallsStatisticsWidget",
  data() {
    const today = new Date().toISOString().split("T")[0];
    const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    return {
      loading: false,
      error: null,
      stats: null,
      dateFrom: monthAgo,
      dateTo: today,
      activeTab: 0,
    };
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const result = await NSApi.instance.getCallsStatistics({
          date_from: this.dateFrom,
          date_to: this.dateTo,
        });
        if (result.success) {
          this.stats = result.data;
        } else {
          this.error = "Ошибка получения данных";
        }
      } catch (e) {
        this.error = e.response?.data?.error || "Ошибка загрузки статистики";
      } finally {
        this.loading = false;
      }
    },
    pct(val, total) {
      if (!total) return 0;
      return Math.round((val / total) * 100);
    },
    statusColor(status) {
      const map = {
        "В работе": "success",
        "Ответ клиента": "info",
        "В очереди": "warning",
        "Новая/Ожидание": "grey",
      };
      return map[status] || "primary";
    },
    waitDaysColor(days) {
      if (days <= 1) return "success--text";
      if (days <= 3) return "warning--text";
      return "error--text";
    },
    waitDaysChipColor(days) {
      if (days <= 1) return "success";
      if (days <= 3) return "warning";
      return "error";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toLocaleString("ru-RU");
      } catch {
        return dateStr;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.widget-card {
  border-radius: 12px;
}
.stat-chip {
  border-radius: 8px;
}
.section-label {
  font-size: 11px;
  font-weight: 600;
  color: #757575;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
